import frontendPackage from '../../package.json';
import { hasPermission } from './accessControlCatalog';

export const CURRENT_RELEASE_VERSION = frontendPackage.version;

export type ReleaseNoteAccessRequirement =
  | { type: 'permission'; permission: string }
  | { type: 'anyPermission'; permissions: string[] }
  | { type: 'platformAdmin' };

export type ReleaseNoteKind = 'feature' | 'improvement' | 'fix' | 'security';

/** Ordine di visualizzazione delle categorie nei riepiloghi. */
export const RELEASE_NOTE_KINDS: readonly ReleaseNoteKind[] = ['feature', 'improvement', 'fix', 'security'];

export interface ReleaseNoteItem {
  id: string;
  title: string;
  description: string;
  kind: ReleaseNoteKind;
  requiredAccess: ReleaseNoteAccessRequirement;
}

export interface ReleaseNoteSection {
  id: string;
  title: string;
  requiredAccess: ReleaseNoteAccessRequirement;
  items: ReleaseNoteItem[];
}

export interface ReleaseNote {
  version: string;
  releaseDate: string;
  title: string;
  summary: string;
  sections: ReleaseNoteSection[];
}

export interface ReleaseNotesAccess {
  permissions: string[];
  isPlatformAdmin: boolean;
  isSuperAdmin: boolean;
}

export const releaseNotes: ReleaseNote[] = [
  {
    version: '3.6.3',
    releaseDate: '2026-11-04',
    title: 'Nexus 3.6.3',
    summary: 'Correzioni di accesso a Gestione Ordini e Gestione STDP, messaggi più chiari quando manca una struttura assegnata e ripristino della pagina Carica dati dei Piani Terapeutici.',
    sections: [
      {
        id: 'release-363-ordini-stdp',
        title: 'Ordini e STDP',
        requiredAccess: {
          type: 'anyPermission',
          permissions: ['app:dipendenze.gestione-ordini:view', 'app:dipendenze.gestione-stdp:view'],
        },
        items: [
          {
            id: 'release-363-accesso-amministratori',
            title: 'Accesso per gli amministratori',
            description: 'Gli amministratori assegnati direttamente al proprio account possono di nuovo aprire Gestione Ordini e Gestione STDP, con lo stesso perimetro di strutture già calcolato in Controllo accessi.',
            kind: 'fix',
            requiredAccess: {
              type: 'anyPermission',
              permissions: ['app:dipendenze.gestione-ordini:view', 'app:dipendenze.gestione-stdp:view'],
            },
          },
          {
            id: 'release-363-nessuna-struttura',
            title: 'Messaggio chiaro senza strutture',
            description: 'Gestione Ordini e Gestione STDP compaiono nel menu solo se al tuo profilo è assegnata almeno una struttura. Se apri la pagina senza strutture trovi il messaggio "Nessuna struttura assegnata per questa funzione" al posto di un errore generico.',
            kind: 'improvement',
            requiredAccess: {
              type: 'anyPermission',
              permissions: ['app:dipendenze.gestione-ordini:view', 'app:dipendenze.gestione-stdp:view'],
            },
          },
        ],
      },
      {
        id: 'release-363-piani',
        title: 'Piani Terapeutici',
        requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-carica-dati:view' },
        items: [
          {
            id: 'release-363-carica-dati',
            title: 'Pagina Carica dati',
            description: 'La voce Carica dati di Piani Terapeutici apre di nuovo la propria pagina di caricamento, invece della pagina di un\'altra area.',
            kind: 'fix',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-carica-dati:view' },
          },
        ],
      },
    ],
  },
  {
    version: '3.6.2',
    releaseDate: '2026-11-03',
    title: 'Nexus 3.6.2',
    summary: 'Correzione di Gestione Ordini e Gestione STDP: una terapia con dati non validi non blocca più l\'intera struttura.',
    sections: [
      {
        id: 'release-362-ordini-stdp',
        title: 'Ordini e STDP',
        requiredAccess: {
          type: 'anyPermission',
          permissions: ['app:dipendenze.gestione-ordini:view', 'app:dipendenze.gestione-stdp:view'],
        },
        items: [
          {
            id: 'release-362-terapie-da-verificare',
            title: 'Terapie da verificare',
            description: 'Una terapia programmata senza posologia viene esclusa dall\'elenco e dai conteggi e segnalata come da verificare, con il collegamento all\'assistito in Gestione Terapie, dove si può correggere. Le terapie al bisogno non sono interessate e il resto della struttura resta consultabile.',
            kind: 'fix',
            requiredAccess: {
              type: 'anyPermission',
              permissions: ['app:dipendenze.gestione-ordini:view', 'app:dipendenze.gestione-stdp:view'],
            },
          },
        ],
      },
    ],
  },
  {
    version: '3.6.1',
    releaseDate: '2026-11-02',
    title: 'Nexus 3.6',
    summary: "Nexus 3.6 amplia l'Area Sanitaria con la gestione di ordini e STDP, aggiunge due modi per importare le terapie e riporta l'aggiornamento mensile del catalogo AIFA. Include inoltre aggiornamenti di sicurezza.",
    sections: [
      {
        id: 'release-360-terapie',
        title: 'Import delle terapie',
        requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
        items: [
          {
            id: 'release-360-gisss-pdf',
            title: 'Import da PDF GISSS',
            description: 'Le prescrizioni dei PDF GISSS vengono lette, controllate riga per riga, abbinate all\'assistito e al catalogo AIFA e registrate come terapie.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:create' },
          },
          {
            id: 'release-360-legacy-import',
            title: 'Import dal gestionale precedente',
            description: 'Le terapie copiate dal gestionale precedente possono essere riviste, abbinate agli assistiti e al catalogo AIFA e registrate con la giacenza iniziale. Un controllo preventivo segnala duplicati e blocchi, e ripetere l\'operazione non crea terapie doppie.',
            kind: 'feature',
            requiredAccess: { type: 'platformAdmin' },
          },
          {
            id: 'release-360-as-needed',
            title: 'Terapie al bisogno',
            description: 'Le terapie al bisogno si indicano in modo esplicito e le unità di misura sono uniformi in tutta l\'Area Sanitaria.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
          },
          {
            id: 'release-360-assigned-patients',
            title: 'Solo assistiti assegnati',
            description: 'Le terapie escludono gli assistiti dimessi o non più assegnati alla struttura.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
          },
        ],
      },
      {
        id: 'release-360-ordini-stdp',
        title: 'Ordini e STDP',
        requiredAccess: {
          type: 'anyPermission',
          permissions: ['app:dipendenze.gestione-ordini:view', 'app:dipendenze.gestione-stdp:view'],
        },
        items: [
          {
            id: 'release-360-ordini',
            title: 'Gestione Ordini',
            description: 'Richieste e registro degli ordini dei farmaci in una pagina dedicata dell\'Area Sanitaria.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-ordini:view' },
          },
          {
            id: 'release-360-stdp',
            title: 'Gestione STDP',
            description: 'Elenco dei farmaci e delle consegne STDP, configurazione dei periodi e bozze email di richiesta.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-stdp:view' },
          },
        ],
      },
      {
        id: 'release-360-aifa',
        title: 'Catalogo AIFA',
        requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
        items: [
          {
            id: 'release-360-aifa-monthly',
            title: 'Aggiornamento mensile del catalogo',
            description: 'Il catalogo dei farmaci AIFA torna ad aggiornarsi automaticamente ogni mese, così le ricerche nelle terapie usano l\'elenco più recente.',
            kind: 'fix',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
          },
        ],
      },
      {
        id: 'release-360-gisss',
        title: 'Caricamento GISSS',
        requiredAccess: { type: 'platformAdmin' },
        items: [
          {
            id: 'release-360-gisss-structures',
            title: 'Elenco strutture corretto',
            description: 'Nel caricamento GISSS l\'elenco delle strutture si carica correttamente e mostra solo quelle consentite all\'utente.',
            kind: 'fix',
            requiredAccess: { type: 'platformAdmin' },
          },
        ],
      },
      {
        id: 'release-360-sicurezza',
        title: 'Sicurezza',
        requiredAccess: { type: 'anyPermission', permissions: ['hub:business:view', 'hub:dipendenze:view'] },
        items: [
          {
            id: 'release-360-security-updates',
            title: 'Aggiornamenti di sicurezza',
            description: 'Aggiornate le librerie e rafforzati i controlli di accesso e sui caricamenti di file.',
            kind: 'security',
            requiredAccess: { type: 'anyPermission', permissions: ['hub:business:view', 'hub:dipendenze:view'] },
          },
        ],
      },
    ],
  },
  {
    version: '3.5.0',
    releaseDate: '2026-10-19',
    title: 'Nexus 3.5',
    summary: 'Nexus 3.5 introduce il Manuale AI, la guida conversazionale su Nexus, e rende più preciso l\'hub Dipendenze: sedi inattive nascoste dalle tendine, associazione dei questionari ICF all\'assistito, codici anagrafica con zeri iniziali gestiti e proposta piano dell\'assistente rinnovata.',
    sections: [
      {
        id: 'release-350-manuale-ai',
        title: 'Manuale AI',
        requiredAccess: { type: 'anyPermission', permissions: ['hub:business:view', 'hub:dipendenze:view'] },
        items: [
          {
            id: 'release-350-manuale-ai-modal',
            title: 'Guida conversazionale su Nexus',
            description: 'Il Manuale AI risponde a domande su sezioni, permessi, procedure, dashboard ed errori; si apre dalla sidebar, dalla Home, dall\'Area Dipendenze e dal pannello Supporto.',
            kind: 'feature',
            requiredAccess: { type: 'anyPermission', permissions: ['hub:business:view', 'hub:dipendenze:view'] },
          },
        ],
      },
      {
        id: 'release-350-strutture',
        title: 'Strutture e trasferimenti',
        requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-assistiti:view' },
        items: [
          {
            id: 'release-350-inactive-structures',
            title: 'Sedi inattive nascoste dalle tendine',
            description: 'Le strutture senza un periodo aperto non compaiono più nei selettori di Assistiti, Terapie, MMG, Carica dati, Dashboard e Trasferimenti; lo storico degli assistiti resta completo.',
            kind: 'improvement',
            requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.gestione-assistiti:view', 'app:dipendenze.dashboard:view', 'app:dipendenze.gestione-terapie:view', 'app:dipendenze.gestione-mmg:view'] },
          },
          {
            id: 'release-350-transfer-destinations',
            title: 'Destinazioni solo attive',
            description: 'La sede di destinazione di un trasferimento deve essere una struttura aperta: le sedi chiuse sono escluse dall\'elenco e rifiutate dal servizio.',
            kind: 'security',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-assistiti:view' },
          },
          {
            id: 'release-350-discharged-transferred',
            title: 'Dimessi della sede di partenza',
            description: 'Un assistito trasferito risulta anche tra i dimessi della sede di origine, pur restando in carico presso la nuova sede.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-assistiti:view' },
          },
        ],
      },
      {
        id: 'release-350-icf',
        title: 'Questionari ICF',
        requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.pt-questionari:view', 'app:dipendenze.pt-carica-dati:view'] },
        items: [
          {
            id: 'release-350-associate-questionnaire',
            title: 'Associa ad assistito',
            description: 'Nella sezione "Da associare" un questionario può essere legato a un assistito con scheda sulla struttura di sincronizzazione priva di codice ICF: il codice anagrafica viene salvato su quella scheda.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-questionari:view' },
          },
          {
            id: 'release-350-icf-leading-zeros',
            title: 'Codici con zeri iniziali',
            description: 'Il confronto dei codici anagrafica ignora gli zeri iniziali ("041875" equivale a "41875"); in caso di grafie duplicate per assistiti diversi vince la corrispondenza esatta e i match ambigui non vengono assegnati.',
            kind: 'fix',
            requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.pt-questionari:view', 'app:dipendenze.pt-carica-dati:view'] },
          },
        ],
      },
      {
        id: 'release-350-piani',
        title: 'Piani terapeutici e Assistente IA',
        requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.pt-piani:view', 'app:dipendenze.pt-assistente-ia:view'] },
        items: [
          {
            id: 'release-350-first-pti',
            title: 'Crea primo PTI da ICF',
            description: 'Per i pazienti senza piano compilato il bottone mostra "Crea primo PTI da ICF"; arrivando da Piani l\'assistente trova subito questionari e piano senza dover riselezionare il paziente.',
            kind: 'fix',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-piani:view' },
          },
          {
            id: 'release-350-icf-objective-title',
            title: 'Titoli ICF non modificabili',
            description: 'Il titolo degli obiettivi derivati dall\'analisi ICF rispecchia l\'item del questionario e non può essere modificato.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-piani:view' },
          },
          {
            id: 'release-350-notification-badges',
            title: 'Badge notifiche calibrati',
            description: 'Il badge "Nuovo ICF" scatta dopo 4 mesi dall\'ultima somministrazione e "aggiornamento necessario" dopo 1 mese senza aggiornamenti su alcun obiettivo; le date recenti dell\'assistente sono ora riconosciute.',
            kind: 'fix',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-piani:view' },
          },
          {
            id: 'release-350-proposal-style',
            title: 'Proposta piano rinnovata',
            description: 'La proposta di piano dell\'assistente non usa più emoticon e adotta il nuovo stile grafico con icone e pannelli coerenti con il resto dell\'app.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-assistente-ia:view' },
          },
        ],
      },
    ],
  },
  {
    version: '3.4.0',
    releaseDate: '2026-10-15',
    title: 'Nexus 3.4',
    summary: 'Nexus 3.4 rende più chiaro il Centro Novità e rafforza i flussi Dipendenze: questionari ICF nel perimetro corretto, una sola movimentazione attiva per assistito, controlli più puntuali nei form e stampa PTI più sicura.',
    sections: [
      {
        id: 'release-340-release-center',
        title: 'Centro Novità',
        requiredAccess: { type: 'anyPermission', permissions: ['hub:business:view', 'hub:dipendenze:view'] },
        items: [
          {
            id: 'release-340-release-timeline',
            title: 'Timeline delle versioni',
            description: 'Le versioni sono presentate in una timeline compatta che evidenzia quella in uso e raggruppa le release meno recenti.',
            kind: 'improvement',
            requiredAccess: { type: 'anyPermission', permissions: ['hub:business:view', 'hub:dipendenze:view'] },
          },
          {
            id: 'release-340-release-categories',
            title: 'Riepilogo per tipo di aggiornamento',
            description: 'Le voci delle release notes sono classificate con icone e conteggi per distinguere novità, migliorie, correzioni e sicurezza.',
            kind: 'feature',
            requiredAccess: { type: 'anyPermission', permissions: ['hub:business:view', 'hub:dipendenze:view'] },
          },
        ],
      },
      {
        id: 'release-340-icf',
        title: 'Questionari ICF',
        requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.pt-carica-dati:view', 'app:dipendenze.pt-questionari:view'] },
        items: [
          {
            id: 'release-340-unassigned-scope',
            title: 'Questionari non assegnati nel perimetro',
            description: 'I questionari ICF non associati a un assistito sono visibili e aggiornabili quando la struttura caricante rientra nel perimetro autorizzato; i record legacy senza identità non bloccano più quelli validi.',
            kind: 'security',
            requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.pt-carica-dati:view', 'app:dipendenze.pt-questionari:view'] },
          },
          {
            id: 'release-340-icf-import',
            title: 'Import ICF più resistente',
            description: 'I valori numerici anomali troppo grandi vengono scartati senza interrompere il download del resto del questionario.',
            kind: 'fix',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-carica-dati:view' },
          },
          {
            id: 'release-340-icf-identity',
            title: 'Identificazione assistito più sicura',
            description: 'Un codice anagrafica ambiguo viene segnalato invece di associare il questionario a un assistito non univoco.',
            kind: 'fix',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-questionari:view' },
          },
        ],
      },
      {
        id: 'release-340-assistiti',
        title: 'Assistiti e trasferimenti',
        requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-assistiti:view' },
        items: [
          {
            id: 'release-340-single-movement',
            title: 'Una movimentazione attiva',
            description: 'Per ogni assistito resta una sola richiesta attiva: i duplicati dello stesso tipo vengono bloccati e una nuova procedura sostituisce quella precedente.',
            kind: 'fix',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-assistiti:view' },
          },
          {
            id: 'release-340-transfer-actions',
            title: 'Azioni coerenti con la sede',
            description: 'La destinazione accetta o rifiuta, l’origine può ritirare la propria richiesta e nel centro richieste compare solo la movimentazione attiva più recente.',
            kind: 'security',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-assistiti:view' },
          },
          {
            id: 'release-340-destinations',
            title: 'Destinazioni complete',
            description: 'Il selettore della sede di destinazione mostra tutte le strutture disponibili, senza ampliare il perimetro operativo dell’operatore.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-assistiti:view' },
          },
          {
            id: 'release-340-linked-structures',
            title: 'Aggiornamenti sede più sicuri',
            description: 'Le modifiche alle strutture dell’assistito rispettano il perimetro e tengono conto delle sedi collegate da trasferimenti conclusi.',
            kind: 'security',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-assistiti:view' },
          },
        ],
      },
      {
        id: 'release-340-forms',
        title: 'Assistiti e strutture',
        requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.gestione-assistiti:view', 'app:dipendenze.gestione-strutture:view'] },
        items: [
          {
            id: 'release-340-live-validation',
            title: 'Validazione durante l’inserimento',
            description: 'I campi che bloccherebbero il salvataggio vengono evidenziati mentre si digita e mostrano un messaggio di correzione.',
            kind: 'improvement',
            requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.gestione-assistiti:view', 'app:dipendenze.gestione-strutture:view'] },
          },
          {
            id: 'release-340-edit-cancel',
            title: 'Annullamento delle modifiche',
            description: 'La modifica di un assistito può essere annullata con la X; prima di scartare dati non salvati viene chiesta conferma.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-assistiti:view' },
          },
        ],
      },
      {
        id: 'release-340-piani',
        title: 'Piani terapeutici',
        requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-piani:view' },
        items: [
          {
            id: 'release-340-case-manager',
            title: 'Case manager collegati alla struttura',
            description: 'Nei monitoraggi il case manager può essere scelto tra i membri dei gruppi associati alle strutture attive del paziente.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-piani:view' },
          },
          {
            id: 'release-340-print',
            title: 'Stampa PTI più sicura',
            description: 'Il documento viene preparato in un contesto temporaneo rimosso dopo la stampa, evitando schede bianche e finestre persistenti con dati sensibili.',
            kind: 'security',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-piani:view' },
          },
          {
            id: 'release-340-transactions',
            title: 'Salvataggi più affidabili',
            description: 'Le operazioni transazionali su piani, trasferimenti e richieste ordine sono allineate al comportamento reale di Firestore.',
            kind: 'fix',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-piani:view' },
          },
          {
            id: 'release-340-email-coming-soon',
            title: 'Invio email temporaneamente sospeso',
            description: 'L’invio del PTI via Gmail è disabilitato e mostra “Funzione in arrivo” fino al completamento della configurazione Gmail API.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-piani:view' },
          },
        ],
      },
    ],
  },
  {
    version: '3.3.2',
    releaseDate: '2026-10-09',
    title: 'Nexus 3.3.2',
    summary: 'Nexus 3.3.2 rafforza il controllo dell’Assistente IA distinguendo l’accesso all’app dall’utilizzo delle funzioni basate su modelli generativi.',
    sections: [
      {
        id: 'release-332-ai',
        title: 'Assistente IA',
        requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-assistente-ia:view' },
        items: [
          {
            id: 'release-332-llm-access',
            title: 'Autorizzazione dedicata per le funzioni AI',
            description: 'L’accesso alla pagina Assistente IA non abilita automaticamente le funzioni basate su modelli generativi; l’autorizzazione viene gestita separatamente in base al profilo.',
            kind: 'security',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-assistente-ia:view' },
          },
          {
            id: 'release-332-deterministic-icf',
            title: 'Confronto ICF disponibile',
            description: 'Il confronto deterministico dei questionari ICF resta disponibile anche quando le funzioni LLM non sono abilitate.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-assistente-ia:view' },
          },
        ],
      },
      {
        id: 'release-332-access-control',
        title: 'Configurazione Access Control',
        requiredAccess: { type: 'platformAdmin' },
        items: [
          {
            id: 'release-332-llm-grant',
            title: 'Permesso Utilizzo LLM',
            description: 'I profili possono distinguere l’accesso all’Assistente IA dall’autorizzazione a usare i modelli generativi.',
            kind: 'feature',
            requiredAccess: { type: 'platformAdmin' },
          },
        ],
      },
    ],
  },
  {
    version: '3.3.1',
    releaseDate: '2026-10-09',
    title: 'Nexus 3.3.1',
    summary: 'Nexus 3.3.1 ha corretto la gestione dei profili Access Control e reso più agevoli la ricerca e la consultazione delle terapie.',
    sections: [
      {
        id: 'release-331-access-profiles',
        title: 'Profili Access Control',
        requiredAccess: { type: 'platformAdmin' },
        items: [
          {
            id: 'release-331-unassigned-profiles',
            title: 'Profili modello non assegnati',
            description: 'È possibile salvare un profilo prima di associarlo a gruppi; il salvataggio non avvia aggiornamenti dei gruppi quando non ci sono assegnazioni.',
            kind: 'fix',
            requiredAccess: { type: 'platformAdmin' },
          },
        ],
      },
      {
        id: 'release-331-therapy-ui',
        title: 'Gestione Terapie',
        requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
        items: [
          {
            id: 'release-331-therapy-facilities',
            title: 'Ricerca per struttura',
            description: 'Gli assistiti possono essere filtrati e raggruppati per struttura, con righe e registro terapie più compatti e indicatori di copertura più leggibili.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
          },
        ],
      },
    ],
  },
  {
    version: '3.3.0',
    releaseDate: '2026-10-08',
    title: 'Nexus 3.3',
    summary: 'Nexus 3.3 rende più precisi i perimetri delle strutture e chiarisce come vengono applicati tra Hub, contenitori e singole applicazioni.',
    sections: [
      {
        id: 'release-330-structure-scopes',
        title: 'Strutture e perimetri',
        requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.dashboard:view', 'app:dipendenze.gestione-assistiti:view', 'app:dipendenze.area-sanitaria:view'] },
        items: [
          {
            id: 'release-330-application-scopes',
            title: 'Perimetri per applicazione',
            description: 'Gli accessi alle strutture sono risolti per la singola applicazione di Dipendenze, così Assistiti, Dashboard, MMG e strumenti sanitari possono rispettare perimetri distinti.',
            kind: 'feature',
            requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.dashboard:view', 'app:dipendenze.gestione-assistiti:view', 'app:dipendenze.area-sanitaria:view'] },
          },
          {
            id: 'release-330-scope-inheritance',
            title: 'Ereditarietà più chiara',
            description: 'Uno scope configurato sull’Hub può essere ereditato dalle app; le configurazioni più specifiche sul contenitore o sulla singola app prevalgono.',
            kind: 'improvement',
            requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.dashboard:view', 'app:dipendenze.gestione-assistiti:view', 'app:dipendenze.area-sanitaria:view'] },
          },
          {
            id: 'release-330-mmg-search-scope',
            title: 'Ricerca nel perimetro MMG',
            description: 'La ricerca assistiti e la gestione MMG usano lo scope della propria applicazione.',
            kind: 'security',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-mmg:view' },
          },
        ],
      },
      {
        id: 'release-330-access-admin',
        title: 'Amministrazione degli accessi',
        requiredAccess: { type: 'platformAdmin' },
        items: [
          {
            id: 'release-330-user-structure-scope',
            title: 'Scope individuali configurabili',
            description: 'Il Platform Admin con area Access Control può impostare il perimetro strutture di un utente; le modifiche vengono registrate nell’audit.',
            kind: 'feature',
            requiredAccess: { type: 'platformAdmin' },
          },
          {
            id: 'release-330-group-admin-scope',
            title: 'Perimetro Admin di Dipendenze',
            description: 'I gruppi Admin del Hub Dipendenze ottengono accesso a tutte le strutture del Hub; un override individuale può restringere il perimetro.',
            kind: 'feature',
            requiredAccess: { type: 'platformAdmin' },
          },
          {
            id: 'release-330-scope-editor',
            title: 'Selezione più semplice',
            description: 'Il selettore strutture resta disponibile per le modalità “Tutte” e “Selezionate” e aggiorna automaticamente la modalità in base alla selezione.',
            kind: 'improvement',
            requiredAccess: { type: 'platformAdmin' },
          },
        ],
      },
    ],
  },
  {
    version: '3.2.0',
    releaseDate: '2026-10-06',
    title: 'Nexus 3.2',
    summary: 'Nexus 3.2 rende più chiari gli accessi, amplia gli strumenti dell’Area Dipendenze e rafforza i flussi sanitari e amministrativi che utilizzi ogni giorno.',
    sections: [
      {
        id: 'release-320-access',
        title: 'Accessi e permessi',
        requiredAccess: { type: 'platformAdmin' },
        items: [
          {
            id: 'release-320-effective-access',
            title: 'Accessi più chiari',
            description: 'La gestione degli accessi distingue meglio gruppi, profili, aree amministrative e autorizzazioni individuali.',
            kind: 'improvement',
            requiredAccess: { type: 'platformAdmin' },
          },
          {
            id: 'release-320-structure-scope',
            title: 'Perimetri per struttura',
            description: 'Gli strumenti autorizzati possono essere associati alle strutture pertinenti, mantenendo separati accesso operativo e visibilità delle dashboard.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-strutture:view' },
          },
        ],
      },
      {
        id: 'release-320-dependencies',
        title: 'Dashboard Dipendenze',
        requiredAccess: { type: 'permission', permission: 'app:dipendenze.dashboard:view' },
        items: [
          {
            id: 'release-320-dashboard-scope',
            title: 'Dashboard nel tuo perimetro',
            description: 'La Dashboard Dipendenze mostra le informazioni relative alle strutture disponibili per il tuo profilo, senza confondersi con la Business Dashboard.',
            kind: 'security',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.dashboard:view' },
          },
        ],
      },
      {
        id: 'release-320-assistiti',
        title: 'Gestione Assistiti',
        requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-assistiti:view' },
        items: [
          {
            id: 'release-320-assistiti-search',
            title: 'Anagrafiche più ordinate',
            description: 'La gestione degli assistiti utilizza un contratto coerente e rende più semplice cercare e consultare le anagrafiche nel proprio perimetro.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-assistiti:view' },
          },
        ],
      },
      {
        id: 'release-320-healthcare',
        title: 'Area Sanitaria',
        requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.area-sanitaria:view', 'app:dipendenze.gestione-terapie:view', 'app:dipendenze.gestione-mmg:view'] },
        items: [
          {
            id: 'release-320-therapies',
            title: 'Terapie e approvvigionamento',
            description: 'Le terapie supportano stati, giacenze, movimenti e modalità di approvvigionamento con controlli collegati all’assistito e alla struttura.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
          },
          {
            id: 'release-320-mmg',
            title: 'Gestione MMG',
            description: 'La rubrica MMG consente di consultare i medici e gestire le associazioni con gli assistiti nel perimetro autorizzato.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-mmg:view' },
          },
        ],
      },
      {
        id: 'release-320-icf-plans',
        title: 'ICF e Piani',
        requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.pt-questionari:view', 'app:dipendenze.pt-piani:view'] },
        items: [
          {
            id: 'release-320-icf-scope',
            title: 'Questionari nel contesto corretto',
            description: 'I questionari ICF e i Piani tengono conto dell’assistito e della struttura associata quando previsto dal flusso.',
            kind: 'fix',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-questionari:view' },
          },
          {
            id: 'release-320-plans',
            title: 'Piani più affidabili',
            description: 'La consultazione dei Piani mantiene i controlli di accesso e riduce i caricamenti ripetuti durante il lavoro quotidiano.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-piani:view' },
          },
        ],
      },
      {
        id: 'release-320-administration',
        title: 'Amministrazione e Maintenance',
        requiredAccess: { type: 'platformAdmin' },
        items: [
          {
            id: 'release-320-maintenance',
            title: 'Manutenzione più controllabile',
            description: 'Gli amministratori possono gestire lo stato globale o quello dei singoli Hub con una console dedicata.',
            kind: 'feature',
            requiredAccess: { type: 'platformAdmin' },
          },
        ],
      },
      {
        id: 'release-320-import-stability',
        title: 'Upload e stabilità',
        requiredAccess: { type: 'anyPermission', permissions: ['platform:dataset:read:execute', 'platform:dataset:ingest:execute'] },
        items: [
          {
            id: 'release-320-upload',
            title: 'Caricamenti più affidabili',
            description: 'Il caricamento dei dati rende più chiari i passaggi di trasferimento, verifica e preparazione del dataset.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'platform:dataset:ingest:execute' },
          },
          {
            id: 'release-320-stability',
            title: 'Flussi più stabili',
            description: 'Sono stati migliorati feedback, filtri e gestione degli errori nelle attività operative.',
            kind: 'fix',
            requiredAccess: { type: 'permission', permission: 'platform:dataset:read:execute' },
          },
        ],
      },
    ],
  },
  {
    version: '3.1.0',
    releaseDate: '2026-09-24',
    title: 'Nexus 3.1',
    summary: 'La nuova release introduce il caricamento backend-driven del partitario, la materializzazione dei dataset per la dashboard, il dominio Terapie con catalogo AIFA e una gestione più completa dei flussi amministrativi e Workspace.',
    sections: [
      {
        id: 'sanitario-therapy',
        title: 'Terapie e farmaci AIFA',
        requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
        items: [
          {
            id: 'therapy-backend-domain',
            title: 'Gestione Terapie backend-driven',
            description: 'Il dominio Terapie valida assistito e struttura, gestisce stock teorico, movimenti e ciclo di vita con audit server-side.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
          },
          {
            id: 'therapy-aifa-lookup',
            title: 'Lookup farmaci AIFA',
            description: 'La selezione del farmaco utilizza il catalogo AIFA tramite lookup autorevole server-side e verifica della versione della fonte.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
          },
          {
            id: 'therapy-order-request',
            title: 'OrderRequest per le richieste',
            description: 'Le richieste di prescrizione sono rappresentate nel nuovo dominio OrderRequest con validazioni e stato tracciabile.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
          },
        ],
      },
      {
        id: 'data-import',
        title: 'Importazione dati',
        requiredAccess: { type: 'anyPermission', permissions: ['platform:dataset:read:execute', 'platform:dataset:ingest:execute'] },
        items: [
          {
            id: 'data-import-signed-upload',
            title: 'Caricamento partitario più sicuro',
            description: 'Il file viene trasferito tramite signed URL temporanea senza passare dal limite multipart del backend.',
            kind: 'security',
            requiredAccess: { type: 'permission', permission: 'platform:dataset:ingest:execute' },
          },
          {
            id: 'data-import-processing',
            title: 'Validazione e ingestion visibili',
            description: 'Il flusso mostra upload, validazione, materializzazione Parquet, manifest e dataset pronto per la dashboard.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'platform:dataset:read:execute' },
          },
          {
            id: 'data-import-quality',
            title: 'Controlli sul partitario',
            description: 'Lo schema canonico del partitario, la qualità dei dati e i metadata di origine vengono verificati prima della pubblicazione del dataset.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'platform:dataset:ingest:execute' },
          },
          {
            id: 'data-import-cleanup',
            title: 'Gestione degli artifact',
            description: 'Puoi annullare o archiviare un job e, quando necessario, eliminare CSV sorgente, dataset e Parquet mantenendo l’audit.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'platform:dataset:delete:execute' },
          },
        ],
      },
      {
        id: 'business-dashboard',
        title: 'Dashboard Business',
        requiredAccess: { type: 'permission', permission: 'app:business.dashboard:view' },
        items: [
          {
            id: 'business-dataset-ready',
            title: 'Dataset pronto per l’analisi',
            description: 'I nuovi dataset finanziari vengono registrati con nome originale, manifest, periodi e dimensioni disponibili per le aggregazioni.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:business.dashboard:view' },
          },
        ],
      },
      {
        id: 'workspace',
        title: 'Workspace e autorizzazioni',
        requiredAccess: { type: 'platformAdmin' },
        items: [
          {
            id: 'workspace-sync-super-admin',
            title: 'Sincronizzazione Workspace per Super Admin',
            description: 'Gli utenti Super Admin possono avviare la sincronizzazione di utenti e gruppi Google Workspace.',
            kind: 'feature',
            requiredAccess: { type: 'platformAdmin' },
          },
        ],
      },
    ],
  },
  {
    version: '3.0.1',
    releaseDate: '2026-09-23',
    title: 'Nexus 3.0.1',
    summary: 'Una release di consolidamento dedicata a sicurezza, autorizzazioni, integrità dei dati e stabilità dei flussi amministrativi e operativi.',
    sections: [
      {
        id: 'release-301-security',
        title: 'Sicurezza e autorizzazioni',
        requiredAccess: { type: 'platformAdmin' },
        items: [
          {
            id: 'release-301-perimeters',
            title: 'Perimetrazione più rigorosa',
            description: 'Le operazioni su strutture, assistiti, fornitori e flussi amministrativi applicano controlli server-side più precisi.',
            kind: 'security',
            requiredAccess: { type: 'platformAdmin' },
          },
          {
            id: 'release-301-client-writes',
            title: 'Scritture client ridotte',
            description: 'I flussi sensibili utilizzano API autorizzate invece di scritture dirette verso le collection protette.',
            kind: 'security',
            requiredAccess: { type: 'platformAdmin' },
          },
        ],
      },
      {
        id: 'release-301-operations',
        title: 'Stabilità operativa',
        requiredAccess: { type: 'platformAdmin' },
        items: [
          {
            id: 'release-301-plans',
            title: 'Salvataggi dei Piani più affidabili',
            description: 'Il versionamento ottimistico riduce il rischio di sovrascrivere modifiche effettuate da un altro operatore.',
            kind: 'fix',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-piani:view' },
          },
          {
            id: 'release-301-transfers',
            title: 'Trasferimenti tra strutture',
            description: 'Le autorizzazioni della struttura sorgente e di quella destinataria vengono verificate separatamente.',
            kind: 'security',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-strutture:view' },
          },
        ],
      },
    ],
  },
  {
    version: '3.0.0',
    releaseDate: '2026-09-21',
    title: 'Nexus 3.0',
    summary: 'Nexus è stato aggiornato con una nuova organizzazione degli strumenti, una navigazione più chiara e nuove funzionalità per le attività che utilizzi ogni giorno. Qui trovi soltanto le novità relative agli strumenti a cui hai accesso.',
    sections: [
      {
        id: 'business',
        title: 'Business',
        requiredAccess: { type: 'anyPermission', permissions: ['hub:business:view'] },
        items: [
          {
            id: 'business-periods',
            title: 'Selezione dei periodi',
            description: 'La Dashboard permette di selezionare uno o più trimestri di analisi direttamente dai filtri.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:business.dashboard:view' },
          },
          {
            id: 'business-kpis',
            title: 'KPI e filtri più chiari',
            description: 'La panoramica riunisce indicatori economici, ambiti e filtri CDC in una lettura più ordinata.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:business.dashboard:view' },
          },
          {
            id: 'business-details',
            title: 'Vista Dettagli',
            description: 'La vista Dettagli consente di approfondire categorie e voci VDS partendo dai filtri applicati.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'feature:business.dashboard.dettagli:view' },
          },
          {
            id: 'business-analysis',
            title: 'Confronto e analisi',
            description: 'La vista Analisi raccoglie confronti e letture temporali per interpretare meglio l’andamento dei dati.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'feature:business.dashboard.analisi:view' },
          },
          {
            id: 'business-suppliers',
            title: 'Anagrafica fornitori aggiornata',
            description: 'L’area fornitori riunisce importazione, deduplicazione, contatti e analisi dell’anagrafica.',
            kind: 'improvement',
            requiredAccess: { type: 'permission', permission: 'app:business.fornitori:view' },
          },
        ],
      },
      {
        id: 'dipendenze',
        title: 'Dipendenze',
        requiredAccess: { type: 'anyPermission', permissions: ['hub:dipendenze:view'] },
        items: [
          {
            id: 'dipendenze-dashboard',
            title: 'Dashboard GISSS',
            description: 'La dashboard organizza le viste su accoglienza, strutture ed esiti con filtri per periodo e struttura.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.dashboard:view' },
          },
          {
            id: 'dipendenze-quality',
            title: 'Vista Qualità',
            description: 'La dashboard include una vista qualitativa con indicatori, revisioni e non conformità.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.dashboard:view' },
          },
          {
            id: 'dipendenze-structures',
            title: 'Gestione strutture e periodi',
            description: 'Le strutture raccolgono sedi, periodi di attività, configurazione ICF e gruppi di lavoro collegati.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-strutture:view' },
          },
          {
            id: 'dipendenze-assisted',
            title: 'Gestione assistiti',
            description: 'Le anagrafiche supportano sedi associate e una lettura separata degli assistiti attivi e usciti.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-assistiti:view' },
          },
        ],
      },
      {
        id: 'area-sanitaria',
        title: 'Area sanitaria',
        requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.area-sanitaria:view', 'app:dipendenze.gestione-terapie:view', 'app:dipendenze.gestione-mmg:view'] },
        items: [
          {
            id: 'area-sanitaria-home',
            title: 'Area sanitaria',
            description: 'Un punto di accesso dedicato agli strumenti sanitari del percorso operativo.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.area-sanitaria:view' },
          },
          {
            id: 'area-sanitaria-therapies',
            title: 'Terapie',
            description: 'La gestione delle terapie raccoglie i piani farmacologici e le attività collegate.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
          },
          {
            id: 'area-sanitaria-aifa',
            title: 'Catalogo farmaci AIFA',
            description: 'La gestione delle terapie utilizza il catalogo AIFA aggiornato per selezionare farmaci, AIC e principi attivi con tracciabilità della fonte.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-terapie:view' },
          },
          {
            id: 'area-sanitaria-mmg',
            title: 'MMG',
            description: 'La sezione MMG rende disponibili le guide operative per i Medici di Medicina Generale.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.gestione-mmg:view' },
          },
        ],
      },
      {
        id: 'piani-terapeutici',
        title: 'Piani terapeutici e ICF',
        requiredAccess: { type: 'anyPermission', permissions: ['app:dipendenze.piani-terapeutici:view', 'app:dipendenze.pt-carica-dati:view', 'app:dipendenze.pt-questionari:view', 'app:dipendenze.pt-piani:view', 'app:dipendenze.pt-assistente-ia:view'] },
        items: [
          {
            id: 'piani-therapeutic-area',
            title: 'Area Piani terapeutici',
            description: 'Un accesso riunito ai soli strumenti dei Piani terapeutici autorizzati per il tuo profilo.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.piani-terapeutici:view' },
          },
          {
            id: 'piani-icf-import',
            title: 'Importazione ICF',
            description: 'Il caricamento dati è disponibile per importare i questionari ICF nei flussi previsti.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-carica-dati:view' },
          },
          {
            id: 'piani-questionnaires',
            title: 'Questionari',
            description: 'La gestione dei questionari supporta compilazione e consultazione delle valutazioni.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-questionari:view' },
          },
          {
            id: 'piani-plans',
            title: 'Piani individuali',
            description: 'I piani terapeutici individuali e i relativi monitoraggi sono raccolti in una vista dedicata.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-piani:view' },
          },
          {
            id: 'piani-assistant',
            title: 'Assistente IA',
            description: 'Lo strumento di supporto alla redazione dei PTRI è disponibile nella sua area dedicata.',
            kind: 'feature',
            requiredAccess: { type: 'permission', permission: 'app:dipendenze.pt-assistente-ia:view' },
          },
        ],
      },
      {
        id: 'amministrazione',
        title: 'Amministrazione e Access Control',
        requiredAccess: { type: 'platformAdmin' },
        items: [
          {
            id: 'admin-access-control',
            title: 'Access Control',
            description: 'Il catalogo Hub/App/Feature rende più chiara la gestione degli accessi e delle eccezioni.',
            kind: 'feature',
            requiredAccess: { type: 'platformAdmin' },
          },
          {
            id: 'admin-workspace',
            title: 'Utenti e Workspace',
            description: 'La sincronizzazione dei gruppi Workspace mantiene allineata la base autorizzativa.',
            kind: 'feature',
            requiredAccess: { type: 'platformAdmin' },
          },
          {
            id: 'admin-security',
            title: 'Sicurezza e audit',
            description: 'Le viste amministrative riuniscono audit applicativo, sicurezza e stato operativo.',
            kind: 'security',
            requiredAccess: { type: 'platformAdmin' },
          },
        ],
      },
    ],
  },
  {
    version: '2.1.2',
    releaseDate: '2026-01-28',
    title: 'Nexus 2.1.2',
    summary: 'La navigazione e i colori dell’interfaccia sono stati resi più riconoscibili, con stati attivi e hover più chiari nella sidebar.',
    sections: [],
  },
];

export function isReleaseNoteRequirementSatisfied(
  requirement: ReleaseNoteAccessRequirement,
  access: ReleaseNotesAccess
): boolean {
  if (requirement.type === 'platformAdmin') {
    return access.isPlatformAdmin || access.isSuperAdmin;
  }

  if (requirement.type === 'permission') {
    return hasPermission(access.permissions, requirement.permission);
  }

  return requirement.permissions.some((permission) => hasPermission(access.permissions, permission));
}

export function filterReleaseNotesByAccess(
  notes: readonly ReleaseNote[],
  access: ReleaseNotesAccess
): ReleaseNote[] {
  return notes.map((release) => ({
    ...release,
    sections: release.sections.reduce<ReleaseNoteSection[]>((visibleSections, section) => {
      if (!isReleaseNoteRequirementSatisfied(section.requiredAccess, access)) return visibleSections;

      const visibleItems = section.items.filter((item) =>
        isReleaseNoteRequirementSatisfied(item.requiredAccess, access)
      );
      if (visibleItems.length > 0) {
        visibleSections.push({ ...section, items: visibleItems });
      }
      return visibleSections;
    }, []),
  }));
}

export function findReleaseNote(version: string, notes: readonly ReleaseNote[] = releaseNotes): ReleaseNote | null {
  return notes.find((release) => release.version === version) || null;
}

export type ReleaseTimelineEntry =
  | { type: 'release'; release: ReleaseNote; itemCount: number; kindCounts: ReleaseKindCounts }
  | { type: 'collapsed'; versions: string[]; itemCount: number; kindCounts: ReleaseKindCounts };

export interface ReleaseTimelineOptions {
  selectedVersion: string | null;
  expanded: boolean;
  visibleCount?: number;
}

export function countReleaseItems(release: ReleaseNote): number {
  return release.sections.reduce((total, section) => total + section.items.length, 0);
}

export type ReleaseKindCounts = Record<ReleaseNoteKind, number>;

export function countReleaseItemsByKind(releases: readonly ReleaseNote[]): ReleaseKindCounts {
  const counts: ReleaseKindCounts = { feature: 0, improvement: 0, fix: 0, security: 0 };
  for (const release of releases) {
    for (const section of release.sections) {
      for (const item of section.items) counts[item.kind] += 1;
    }
  }
  return counts;
}

/**
 * Timeline compatta delle versioni: mostra le release più recenti e raggruppa
 * le precedenti in un unico nodo espandibile. La versione selezionata resta
 * sempre visibile.
 */
export function buildReleaseTimeline(
  releases: readonly ReleaseNote[],
  { selectedVersion, expanded, visibleCount = 3 }: ReleaseTimelineOptions
): ReleaseTimelineEntry[] {
  const entries = releases.map((release): ReleaseTimelineEntry => ({
    type: 'release',
    release,
    itemCount: countReleaseItems(release),
    kindCounts: countReleaseItemsByKind([release]),
  }));

  const selectedIndex = releases.findIndex((release) => release.version === selectedVersion);
  const canCollapse = !expanded && releases.length > visibleCount + 1 && selectedIndex < visibleCount;
  if (!canCollapse) return entries;

  const hidden = releases.slice(visibleCount);
  return [
    ...entries.slice(0, visibleCount),
    {
      type: 'collapsed',
      versions: hidden.map((release) => release.version),
      itemCount: hidden.reduce((total, release) => total + countReleaseItems(release), 0),
      kindCounts: countReleaseItemsByKind(hidden),
    },
  ];
}

export function formatReleaseDate(releaseDate: string): string {
  const [year, month, day] = releaseDate.split('-').map(Number);
  return new Intl.DateTimeFormat('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

export function isReleaseUnread(currentVersion: string, lastSeenRelease: string | null): boolean {
  return Boolean(currentVersion) && currentVersion !== lastSeenRelease;
}

export function shouldAutoOpenReleaseNotes(
  currentVersion: string,
  lastSeenRelease: string | null,
  sessionDismissedVersion: string | null
): boolean {
  return isReleaseUnread(currentVersion, lastSeenRelease) && sessionDismissedVersion !== currentVersion;
}
